import React, { useEffect, useState } from "react";
import { Hammer, Loader2, Eye } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PriceBreakdown } from "@/components/PriceBreakdown";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = [
  { value: "pending_review", label: "Pending Review", cls: "bg-[#FBF1DF] text-[#7A4B12] border border-[#E7D0A8]" },
  { value: "contacted", label: "Contacted", cls: "bg-[#EAF1F7] text-[#1E3E5C] border border-[#C7D7E6]" },
  { value: "quoted", label: "Quoted", cls: "bg-[hsl(var(--gold-light))] text-[hsl(var(--gold-dark))] border border-[hsl(var(--gold-dark))]/30" },
  { value: "converted", label: "Converted", cls: "bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]" },
  { value: "closed", label: "Closed", cls: "bg-[#F4EBDD] text-muted-foreground border border-border" },
];
const statusMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];

function customizationSummary(c) {
  if (!c) return "—";
  const bits = [c.karat, c.gold_color ? `${c.gold_color} Gold` : null,
    c.diamond_type ? (c.diamond_type === "lab" ? "Lab-Grown" : "Natural") : null,
    c.ring_size ? `Size ${c.ring_size}` : null, c.mangalsutra_length_in ? `${c.mangalsutra_length_in}"` : null];
  return bits.filter(Boolean).join(" · ") || "—";
}

export default function CustomBuildRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [statusEdit, setStatusEdit] = useState("");
  const [noteEdit, setNoteEdit] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const { data } = await api.get("/admin/custom-build-requests", { params });
      setRequests(data.requests || []);
    } catch {
      toast.error("Failed to load custom build requests");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = (r) => { setSelected(r); setStatusEdit(r.status); setNoteEdit(r.staff_note || ""); };

  const saveStatus = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/custom-build-requests/${selected.id}`, { status: statusEdit, staff_note: noteEdit });
      toast.success("Request updated");
      setSelected(null);
      load();
    } catch {
      toast.error("Failed to update request");
    } finally { setSaving(false); }
  };

  return (
    <AdminShell title="Custom Build Requests">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Hammer className="h-5 w-5 text-[hsl(var(--gold-dark))]" />
          <p className="text-sm text-muted-foreground">Made-to-order requests submitted from the "Customize Your Piece" section on the storefront.</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="custom-requests-status-filter" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden" data-testid="custom-requests-table">
        {loading ? (
          <div className="p-6 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : requests.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm" data-testid="custom-requests-empty-state">
            No custom build requests {statusFilter !== "all" ? `with status "${statusMeta(statusFilter).label}"` : "yet"}.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Shop</TableHead><TableHead>Product</TableHead>
                <TableHead>Customization</TableHead><TableHead>Qty</TableHead>
                <TableHead className="text-right">Est. Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((r) => (
                <TableRow key={r.id} data-testid="custom-request-row" className="hover:bg-[#F7F0E6]">
                  <TableCell className="text-xs whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-sm">{r.shop_name}</TableCell>
                  <TableCell className="text-sm">{r.product_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{customizationSummary(r.customization)}</TableCell>
                  <TableCell className="text-sm">{r.quantity}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatINR(r.estimated_total)}</TableCell>
                  <TableCell><Badge className={statusMeta(r.status).cls}>{statusMeta(r.status).label}</Badge></TableCell>
                  <TableCell>
                    <Button data-testid="custom-request-view-button" size="sm" variant="ghost" className="gap-1.5" onClick={() => openDetail(r)}>
                      <Eye className="h-3.5 w-3.5" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="custom-request-detail-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-display text-xl">{selected.product_name}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Shop</span><span>{selected.shop_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Quantity</span><span>{selected.quantity}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Requested</span><span>{(selected.created_at || "").slice(0, 16).replace("T", " ")}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Production Time</span><span>{selected.production_days} days</span></div>
                <div className="rounded-lg border border-border bg-[#F7F0E6] p-3">
                  <div className="text-xs text-muted-foreground mb-1">Customization</div>
                  <div className="font-mono text-sm">{customizationSummary(selected.customization)}</div>
                </div>
                {selected.note && (
                  <div className="rounded-lg border border-border p-3">
                    <div className="text-xs text-muted-foreground mb-1">Buyer Note</div>
                    <div className="text-sm">{selected.note}</div>
                  </div>
                )}
                <PriceBreakdown pricing={selected.pricing_snapshot} />
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={statusEdit} onValueChange={setStatusEdit}>
                    <SelectTrigger data-testid="custom-request-status-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Staff Note</Label>
                  <Textarea data-testid="custom-request-staff-note" value={noteEdit} onChange={(e) => setNoteEdit(e.target.value)} rows={2} className="mt-1" placeholder="Internal notes, quote details, follow-up plan..." />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
                <Button data-testid="custom-request-save-button" onClick={saveStatus} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
